import { UserRole } from '.';
import { UserPermission } from './permissions.types';

export type ResponseType<T> = {
  message: string;
  error: boolean;
  data: T;
};

// eslint-disable-next-line @typescript-eslint/ban-types
export type PaginationParams<T = {}> = {
  page: number | string;
  limit: number | string;
} & T;

// eslint-disable-next-line @typescript-eslint/ban-types
export type DateRangeParams<T = {}> = {
  from: string | Date;
  to: string | Date;
} & T;

export enum SortByDate {
  ASC = 'asc',
  DESC = 'desc',
}

export interface Coordinates {
  latitude: number;
  longitude: number;
}

export type ReportExportStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed';

export interface ReportExportResponse {
  id: number;
  status: ReportExportStatus;
  downloadUrl: string | null;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface ReportExportFinishedPayload {
  reportExportId: number;
  status: ReportExportStatus;
  fail: boolean;
}

export interface PhoneObject {
  nationalNumber: string;
  number: string;
}

export interface LocationImageEntity {
  id: number;
  url: string;
  type: 'voidCheck';
  createdAt: string;
}

export type ProviderValue = {
  provider: Provider;
  value: number;
};

export type SalesStatics = {
  count: ProviderValue[];
  total: ProviderValue[];
  virtual: ProviderValue[];
  cancelled: ProviderValue[];
  tax: ProviderValue[];
  recovered: ProviderValue[];
  totalCombined: ProviderValue[];
  virtualCount: ProviderValue[];
  taxWithoutVR: ProviderValue[];
};

export enum AdvancedQueryTypes {
  ALL = 'all',
  ALL_LOCATIONS = 'all_locations',
  ALL_V_GRUBS_ONLY = 'all_v_grubs_only',
  ALL_TDOC_ONLY = 'all_tdoc_only',
  ALL_VPC_ONLY = 'all_vpc_only',
  ALL_VDC_ONLY = 'all_vdc_only',
  ALL_FJ_ONLY = 'all_fj_only',
  ALL_RB_ONLY = 'all_rb_only',
  ALL_TLB_ONLY = 'all_tlb_only',
  LOCATION_SPECIFIC_WITHOUT_ANY_BRAND = 'location_specific_without_any_brand',
  LOCATION_SPECIFIC_WITHOUT_V_GRUBS = 'location_specific_without_v_grubs',
  LOCATION_SPECIFIC_WITH_V_GRUBS = 'location_specific_with_v_grubs',
  LOCATION_SPECIFIC_ONLY_V_GRUBS = 'location_specific_only_grubs',
  V_GRUBS_BRAND_SPECIFIC = 'v_grubs_brand_specific',
  LOCATION_BRAND_SPECIFIC = 'location_brand_specific',
}

export enum Provider {
  doordash = 'doordash',
  doordashPos = 'doordashPos',
  grubhub = 'grubhub',
  grubhubPos = 'grubhubPos',
  gloriafood = 'gloriafood',
  ubereats = 'ubereats',
  chownow = 'chownow',
  delivery = 'delivery',
  eatstreet = 'eatstreet',
  menufy = 'menufy',
  localForYou = 'localForYou',
  vGrubs = 'vGrubs',
  nabe = 'nabe',
  bytes = 'bytes',
  kanekt365 = 'kanekt365',
  vGrubsAI = 'vGrubsAI',
  hyperzod = 'hyperzod',
  devourNow = 'devourNow',
  // unknown = 'unknown',
}

/**
 * The backend's full `ProviderType` enum. `Provider` above is the narrower list
 * the rest of the admin works with (menus, filters, stats) - widening it would
 * change every `Object.values(Provider)` dropdown. Payout provider rules accept
 * any value the backend knows, so they are typed against this instead.
 * Keep in sync with `ProviderType` in the API spec.
 */
export enum ProviderType {
  doordash = 'doordash',
  doordashPos = 'doordashPos',
  grubhub = 'grubhub',
  grubhubPos = 'grubhubPos',
  gloriafood = 'gloriafood',
  ubereats = 'ubereats',
  chownow = 'chownow',
  delivery = 'delivery',
  eatstreet = 'eatstreet',
  menufy = 'menufy',
  localForYou = 'localForYou',
  fleksa = 'fleksa',
  nabe = 'nabe',
  foodhub = 'foodhub',
  cloudwaitress = 'cloudwaitress',
  flipdish = 'flipdish',
  caviar = 'caviar',
  yelp = 'yelp',
  seamless = 'seamless',
  direct = 'direct',
  groupon = 'groupon',
  unknown = 'unknown',
  eat24 = 'eat24',
  tapingo = 'tapingo',
  toast = 'toast',
  bytes = 'bytes',
  vGrubsAI = 'vGrubsAI',
  kanekt365 = 'kanekt365',
  hyperzod = 'hyperzod',
  devourNow = 'devourNow',
}

export enum VDriveProvider {
  relay = 'relay',
  doordash = 'doordash',
  uber = 'uber',
  ally = 'ally',
  cutcats = 'cutcats',
  dlivrd = 'dlivrd',
  motoclick = 'motoclick',
  dedicatedDriver = 'dedicatedDriver',
  grubhub = 'grubhub',
  redwagon = 'redwagon',
  deliveryNow = 'deliveryNow',
  /** Our own fleet (vdrive-backend), shown as "vDrive". */
  vdriveFleet = 'vdriveFleet',
}

export enum ProviderStatus {
  online = 'online',
  offline = 'offline',
  unknown = 'unknown',
}

export enum ProviderConnectionStatus {
  waitingMenu = 'waiting_menu',
  inProgress = 'in_progress',
  waiting = 'waiting',
  connected = 'connected',
  rejected = 'rejected',
  disabled = 'disabled',
}

export enum TransactionStatus {
  pending = 'pending',
  processed = 'processed',
  cancelled = 'cancelled',
  failed = 'failed',
}

export enum VDriveStatus {
  inactive = 'inactive',
  inProgress = 'inProgress',
  active = 'active',
  rejected = 'rejected',
  disabled = 'disabled',
}

export enum LocationStatus {
  ACTIVE = 'active',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  HISTORY = 'history',
}

export enum DeliveryCreationType {
  order = 'order',
  manual = 'manual',
}

/** Cloudflare Turnstile token, single-use and valid for 300s. */
export type Captcha = {
  token: string;
};

export enum Currency {
  usd = 'usd',
}

export type RouteObjectType = {
  path: string;
  Page?: React.ComponentType;
  name?: string;
  Icon?: React.ComponentType;
  /** Roles allowed to reach the route. Checked first. */
  permissions: UserRole[];
  /**
   * Optional second gate, on top of the role check. When set, the user must
   * also hold this permission (see `usePermissions`) for the route to render
   * and for the sidebar entry to appear.
   */
  permission?: UserPermission;
  newItem?: string;
  /**
   * Optional count badge for the sidebar entry, rendered both on the
   * collapsed rail's icon and beside the expanded row's name. A component
   * rather than a number so the app owns the query behind it (the shared
   * `SidebarCountBubble` draws it); it renders nothing at zero.
   */
  Badge?: React.ComponentType;
};

export type OrderVDriveCancellationFeeResponse = {
  cancellationFee: number;
  isDriverAssigned: boolean;
};

export enum MaxRange {
  month = 'month',
  year = 'year',
}

export type VPayBalanceResponse = {
  balance: number;
  overHundredCount: number;
  totalBlueCardBalance: number;
  pendingBlueCardWithrawal: number;
  completedBlueCardWithrawal: number;
};

export type ProviderConnect = {
  provider: Provider;
  id: string | number;
  pathPrefix: 'locations' | 'brands';
};

export enum QueryStatus {
  PENDING = 'pending',
  SUCCESS = 'success',
  ERROR = 'error',
}
